import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '@/shared/utils/chunkLoadRecovery';

/** Skip Sentry only for local `pnpm dev` (Vite mode `development`). Do not use `import.meta.env.DEV`: it is true for any dev server, including `pnpm dev:alpha`, which would wrongly disable Sentry on an alpha URL. */
const isViteDevelopmentMode = (): boolean => import.meta.env.MODE === 'development';

const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
};

const SENTRY_NOISE_PATTERNS: RegExp[] = [
  /Microsoft Clarity.*not being collected/i,
  /wasm streaming compile failed/i,
  /falling back to ArrayBuffer instantiation/i,
  /\[vite\] Failed to reload/i,
  /React has detected a change in the order of Hooks/i,
  /Internal React error: Expected static flag was missing/i,
  /The above error occurred in the </i,
];

function getEventMessage(event: Sentry.ErrorEvent): string {
  if (event.message) {
    return event.message;
  }

  const exceptionMessage = event.exception?.values
    ?.map((entry) => entry.value ?? entry.type ?? '')
    .join(' ');
  if (exceptionMessage) {
    return exceptionMessage;
  }

  const args = event.extra?.arguments;
  if (Array.isArray(args)) {
    return args
      .map((arg) => {
        if (typeof arg === 'string') {
          return arg;
        }
        if (arg && typeof arg === 'object' && 'message' in arg) {
          return String((arg as { message: unknown }).message);
        }
        return '';
      })
      .join(' ');
  }

  return '';
}

function isSentryNoise(event: Sentry.ErrorEvent): boolean {
  const message = getEventMessage(event);

  if (!message) {
    return false;
  }

  if (SENTRY_NOISE_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }

  return isChunkLoadError(message);
}

/** Filters junk URLs / transaction names (e.g. Storybook iframe, malformed router state). */
const isValidValue = (value: string | undefined | null): boolean => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return (
    !value.includes('/undefined/') &&
    !value.includes('undefined') &&
    !value.includes('iframe.html') &&
    value.trim() !== '' &&
    value !== 'undefined'
  );
};

function buildTracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = ['localhost'];

  const apiUrl = import.meta.env.VITE_APP_API_URL;
  if (apiUrl) {
    try {
      targets.push(new URL(apiUrl).origin);
    } catch {
      // Invalid VITE_APP_API_URL — skip; tracing still works for other targets.
    }
  }

  // Do not add Keycloak / account hosts. Sentry injects `sentry-trace` and `baggage` on
  // matching fetches; those headers are not in Keycloak's Access-Control-Allow-Headers,
  // so token refresh fails (CORS) and auth can loop on login/refresh.

  return targets;
}

export const initSentry = (): void => {
  if (isViteDevelopmentMode() || isLocalhost()) {
    console.log('[Sentry] Local development host, skipping Sentry initialization');
    return;
  }

  const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;

  if (!sentryDsn || sentryDsn.trim() === '') {
    console.log('[Sentry] DSN not provided, skipping Sentry initialization');
    return;
  }

  // Prefer VITE_APP_ENV (from .env.[mode] per build:alpha / build:production) over
  // VITE_DEPLOYMENT_STATE. In Vite, shell env overrides .env files; a global CI
  // VITE_DEPLOYMENT_STATE would otherwise win here and mask production as "alpha".
  const sentryEnvironment =
    import.meta.env.VITE_APP_ENV ||
    import.meta.env.VITE_DEPLOYMENT_STATE ||
    import.meta.env.MODE ||
    'production';

  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_CI_COMMIT_TAG,
    environment: sentryEnvironment,
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',

    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.5,
    profilesSampleRate: 0.1,

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        instrumentPageLoad: true,
        // Safe with `withSentryReactRouterV7Routing`: parameterized route names, fewer bad transactions than legacy setups.
        instrumentNavigation: true,
      }),
      Sentry.browserSessionIntegration(),
      Sentry.browserProfilingIntegration(),
      // Errors only — third-party warn noise (Clarity, wasm) is filtered in beforeSend too.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.extraErrorDataIntegration(),
    ],

    tracePropagationTargets: buildTracePropagationTargets(),

    ignoreErrors: [
      'Data from this session is not being collected by Microsoft Clarity due to your configured project settings',
      'wasm streaming compile failed: TypeError: WebAssembly compilation aborted: Network error: Response body loading was aborted',
      'falling back to ArrayBuffer instantiation',
      /Failed to fetch dynamically imported module/,
      /Loading chunk [\d]+ failed/,
      /\[vite\] Failed to reload/,
    ],

    beforeSendTransaction(transaction) {
      const name = transaction.transaction;
      const url = transaction.request?.url;

      if (!isValidValue(name)) {
        return null;
      }
      if (url != null && url !== '' && !isValidValue(url)) {
        return null;
      }

      return transaction;
    },

    beforeSend(event) {
      if (isSentryNoise(event)) {
        return null;
      }

      const url = event.request?.url;
      if (url != null && url !== '' && !isValidValue(url)) {
        return null;
      }

      const transaction = event.transaction;
      if (typeof transaction === 'string' && transaction.length > 0 && !isValidValue(transaction)) {
        return null;
      }

      if (event.type === 'replay_event' || event.type === 'replay_recording') {
        const browserContext = event.contexts?.browser as { url?: string } | undefined;
        const replayUrl = event.request?.url || browserContext?.url;
        if (replayUrl != null && replayUrl !== '' && !isValidValue(replayUrl)) {
          return null;
        }
      }

      return event;
    },
  });

  console.log('[Sentry] Initialized successfully', {
    environment: sentryEnvironment,
    viteMode: import.meta.env.MODE,
    release: import.meta.env.VITE_CI_COMMIT_TAG ?? null,
  });

  if (import.meta.env.VITE_CI_COMMIT_TAG) {
    Sentry.setTag('ci_tag', import.meta.env.VITE_CI_COMMIT_TAG);
  }
};
